import React, { useState, useRef, useCallback } from 'react';
import { ChevronDown, LogOut, Sun, Moon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const Navbar = ({ onNavigate }) => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [iconKey, setIconKey] = useState(0);
  const toggleRef = useRef(null);

  const handleNavigation = (page) => {
    onNavigate(page);
    setDropdownOpen(false);
  };

  // Create sparkle particles around the toggle button
  const createSparkles = useCallback((e) => {
    const button = toggleRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const colors = theme === 'light'
      ? ['#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#60a5fa']
      : ['#f59e0b', '#f97316', '#fb923c', '#fbbf24', '#fcd34d'];

    for (let i = 0; i < 8; i++) {
      const sparkle = document.createElement('div');
      sparkle.className = 'theme-sparkle';
      const angle = (Math.PI * 2 * i) / 8;
      const distance = 20 + Math.random() * 20;
      sparkle.style.setProperty('--tx', `${Math.cos(angle) * distance}px`);
      sparkle.style.setProperty('--ty', `${Math.sin(angle) * distance}px`);
      sparkle.style.left = `${centerX}px`;
      sparkle.style.top = `${centerY}px`;
      sparkle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      document.body.appendChild(sparkle);
      setTimeout(() => sparkle.remove(), 700);
    }
  }, [theme]);

  const handleToggle = useCallback((e) => {
    createSparkles(e);
    setIconKey(prev => prev + 1); // Force re-mount for icon spin animation
    toggleTheme(e); // Pass the event so the ripple originates from the click position
  }, [toggleTheme, createSparkles]);

  return (
    <nav className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Left Side: Home Link */}
          <div className="flex items-center">
            <button onClick={() => onNavigate('home')} className="group hover:opacity-80 transition-opacity">
              <span className="font-bold text-xl bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-amber-500">
                Creyente AI Agents
              </span>
            </button>
          </div>

          {/* Right Side: Theme Toggle, User Profile & Logout */}
          <div className="flex items-center gap-4">
            {/* Theme Toggle */}
            <button
              ref={toggleRef}
              onClick={handleToggle}
              className="theme-toggle-glow relative w-14 h-7 bg-gray-200 dark:bg-gray-700 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              aria-label="Toggle theme"
            >
              <div className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white dark:bg-gray-800 rounded-full shadow-md transform transition-transform duration-300 flex items-center justify-center ${theme === 'dark' ? 'translate-x-7' : 'translate-x-0'}`}>
                <div key={iconKey} className="theme-toggle-icon-enter">
                  {theme === 'light' ? (
                    <Sun size={14} className="text-orange-500" />
                  ) : (
                    <Moon size={14} className="text-blue-400" />
                  )}
                </div>
              </div>
            </button>

            {user && (
              <div className="flex items-center gap-3 bg-orange-50 dark:bg-orange-900/20 rounded-full pl-1 pr-4 py-1 border border-orange-100 dark:border-orange-800">
                {/* Avatar */}
                <div className="relative">
                  <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm shadow-sm ring-2 ring-white dark:ring-gray-800">
                    {(user.name || user.email || user.username)?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-gray-900"></div>
                </div>

                {/* Name - Hidden on mobile, shown on tablet+ */}
                <div className="hidden md:flex flex-col">
                  <span className="text-gray-800 dark:text-gray-200 font-semibold text-sm leading-tight">
                    {user.name || user.email || user.username}
                  </span>
                </div>
              </div>
            )}

            <button
              onClick={logout}
              className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
              title="Sign Out"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;